import React, { useState, useRef, useEffect } from 'react'
import {
  Inbox,
  Send,
  Archive,
  Trash2,
  Star,
  Search,
  Plus,
  MoreHorizontal,
  Reply,
  ReplyAll,
  Forward,
  Paperclip,
  Maximize2,
  X,
  FileText,
  ChevronDown,
  Loader2,
  Mail,
  RefreshCw,
  AlertCircle,
  Printer,
  Bold,
  Italic,
  Underline,
  Smile,
  Clock,
  Download,
} from 'lucide-react'
import { useWebmail, useEmails, useEmailContent, useFolders, useSendEmail, useEmailActions } from '@/lib/data/hooks/useWebmail'
import { MailMessage, MailFolder, MailAttachment, mailApi } from '@/lib/supabase/mail'
import { EmailAccount } from '@/lib/data/hooks/useEmailAccounts'
import { EmailContentFrame } from './EmailContentFrame'

const FOLDER_ICONS: Record<string, React.ElementType> = {
  inbox: Inbox,
  sent: Send,
  drafts: FileText,
  archive: Archive,
  spam: AlertCircle,
  trash: Trash2,
  custom: Mail,
}

export function WebmailView() {
  const {
    accounts,
    selectedAccount,
    selectedAccountId,
    setSelectedAccountId,
    accountsLoading,
    selectedFolder,
    setSelectedFolder,
    selectedEmailUid,
    setSelectedEmailUid,
  } = useWebmail()

  const { emails, isLoading: emailsLoading, refresh, totalCount } = useEmails(selectedAccountId, { folder: selectedFolder })
  const { email: selectedEmail, isLoading: emailLoading } = useEmailContent(selectedAccountId, selectedEmailUid, selectedFolder)
  const { folders } = useFolders(selectedAccountId)
  const { sendEmail, isLoading: sendingEmail } = useSendEmail()
  const { performAction } = useEmailActions()

  const [searchTerm, setSearchTerm] = useState('')
  const [isComposeOpen, setIsComposeOpen] = useState(false)
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false)
  const accountDropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (accountDropdownRef.current && !accountDropdownRef.current.contains(event.target as Node)) {
        setIsAccountDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  const [composeData, setComposeData] = useState({
    to: '',
    cc: '',
    subject: '',
    body: '',
  })

  // Reply dialog state
  const [isReplyOpen, setIsReplyOpen] = useState(false)
  const [replyMode, setReplyMode] = useState<'reply' | 'replyAll' | 'forward'>('reply')
  const [replyContent, setReplyContent] = useState('')
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-resize reply textarea
  useEffect(() => {
    if (replyTextareaRef.current && isReplyOpen) {
      replyTextareaRef.current.style.height = 'auto'
      replyTextareaRef.current.style.height = Math.max(150, replyTextareaRef.current.scrollHeight) + 'px'
    }
  }, [replyContent, isReplyOpen])

  // Reset reply when email changes
  useEffect(() => {
    setIsReplyOpen(false)
    setReplyContent('')
    setReplyMode('reply')
  }, [selectedEmailUid])


  // Filter emails based on search term, and merge attachment info from selected email
  const filteredEmails = emails
    .filter(email =>
      email.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      email.from.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (email.from.name?.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .map(email => {
      // If this is the selected email and we have more detailed info, use it
      if (selectedEmail && email.uid === selectedEmail.uid) {
        return {
          ...email,
          hasAttachments: selectedEmail.hasAttachments || email.hasAttachments,
        }
      }
      return email
    })

  const handleSendEmail = async () => {
    if (!selectedAccountId || !composeData.to || !composeData.subject) return

    const success = await sendEmail({
      accountId: selectedAccountId,
      to: composeData.to.split(',').map(e => e.trim()),
      cc: composeData.cc ? composeData.cc.split(',').map(e => e.trim()) : undefined,
      subject: composeData.subject,
      body: composeData.body,
    })

    if (success) {
      setIsComposeOpen(false)
      setComposeData({ to: '', cc: '', subject: '', body: '' })
    }
  }

  const handleMarkRead = async (uid: string) => {
    if (!selectedAccountId) return
    await performAction(selectedAccountId, [uid], 'markRead')
    refresh()
  }

  const handleStar = async (uid: string, isStarred: boolean) => {
    if (!selectedAccountId) return
    await performAction(selectedAccountId, [uid], isStarred ? 'unstar' : 'star')
    refresh()
  }

  const handleDelete = async (uid: string) => {
    if (!selectedAccountId) return
    await performAction(selectedAccountId, [uid], 'delete')
    setSelectedEmailUid(null)
    refresh()
  }

  // Download attachment handler
  const handleDownloadAttachment = async (attachment: MailAttachment) => {
    if (!attachment.downloadId) {
      console.error('No downloadId for attachment:', attachment.filename)
      return
    }

    try {
      const { filename, contentType, content } = await mailApi.downloadAttachment(attachment.downloadId)

      // Decode base64 and create blob
      const byteCharacters = atob(content)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: contentType })

      // Create download link and trigger
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download attachment:', err)
    }
  }

  // No accounts configured
  if (!accountsLoading && accounts.length === 0) {
    return (
      <div className="flex h-[calc(100vh-140px)] bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl overflow-hidden items-center justify-center">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 bg-[var(--bg-element)] rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail size={32} className="text-[var(--text-muted)]" />
          </div>
          <h2 className="text-xl font-bold text-[var(--text-main)] mb-2">No Email Accounts</h2>
          <p className="text-[var(--text-muted)] mb-4">
            Configure email accounts in Settings to start using Webmail.
          </p>
          <p className="text-sm text-[var(--text-muted)]">
            Go to Settings → Email Accounts to add your DreamHost email credentials.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-140px)] bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl overflow-hidden animate-fade-in relative shadow-2xl">
      {/* 1. Navigation Pane (Left) */}
      <div className="w-56 bg-[var(--bg-element)] border-r border-[var(--border)] flex flex-col p-4">
        {/* Account Selector */}
        <div className="mb-6 relative" ref={accountDropdownRef}>
          <button
            onClick={() => setIsAccountDropdownOpen(!isAccountDropdownOpen)}
            className="w-full flex items-center justify-between px-3 py-2 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg text-sm font-medium text-[var(--text-main)] hover:border-[var(--primary)] transition-colors"
          >
            <span className="truncate">
              {selectedAccount?.emailAddress || 'Select Account'}
            </span>
            <ChevronDown size={14} className={`text-[var(--text-muted)] transition-transform ${isAccountDropdownOpen ? 'rotate-180' : ''}`} />
          </button>

          {isAccountDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-[var(--bg-card)] border border-[var(--border)] rounded-lg shadow-xl overflow-hidden z-20 animate-fade-in">
              {accounts.map(account => (
                <button
                  key={account.id}
                  onClick={() => {
                    setSelectedAccountId(account.id)
                    setIsAccountDropdownOpen(false)
                  }}
                  className={`w-full text-left px-3 py-2.5 text-xs hover:bg-[var(--bg-hover)] transition-colors ${
                    selectedAccountId === account.id
                      ? 'text-[var(--primary)] font-bold bg-[var(--bg-element)]'
                      : 'text-[var(--text-muted)]'
                  }`}
                >
                  {account.emailAddress}
                </button>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={() => setIsComposeOpen(true)}
          className="flex items-center justify-center gap-2 w-full py-3 bg-[var(--primary)] text-white rounded-xl mb-6 hover:opacity-90 transition-opacity font-medium shadow-lg shadow-[var(--primary)]/20"
        >
          <Plus size={18} /> Compose
        </button>

        {/* Folders */}
        <div className="space-y-1">
          {folders.map(folder => {
            const FolderIcon = FOLDER_ICONS[folder.type] || Mail
            const isSelected = selectedFolder === folder.path
            return (
              <button
                key={folder.id}
                onClick={() => {
                  setSelectedFolder(folder.path)
                  setSelectedEmailUid(null)
                }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isSelected
                    ? 'bg-[var(--bg-card)] text-[var(--text-main)] shadow-sm'
                    : 'text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text-main)]'
                }`}
              >
                <div className="flex items-center gap-3">
                  <FolderIcon size={16} className={isSelected ? 'text-[var(--primary)]' : ''} />
                  {folder.name}
                </div>
                {folder.unreadCount > 0 && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      isSelected
                        ? 'bg-[var(--bg-element)] text-[var(--text-main)]'
                        : 'bg-[var(--bg-card)] text-[var(--text-muted)]'
                    }`}
                  >
                    {folder.unreadCount}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* Labels section */}
        <div className="mt-auto pt-4 border-t border-[var(--border)]">
          <h4 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-2">
            Labels
          </h4>
          <div className="space-y-2">
            {['Important', 'Work', 'Personal'].map(label => (
              <div
                key={label}
                className="flex items-center gap-2 text-sm text-[var(--text-muted)] cursor-pointer hover:text-[var(--text-main)]"
              >
                <div
                  className={`w-2 h-2 rounded-full ${
                    label === 'Important'
                      ? 'bg-rose-500'
                      : label === 'Work'
                      ? 'bg-blue-500'
                      : 'bg-emerald-500'
                  }`}
                />
                {label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 2. Message List (Middle/Full) */}
      <div className={`${selectedEmailUid ? 'w-96 border-r border-[var(--border)]' : 'flex-1'} flex flex-col bg-[var(--bg-card)]`}>
        <div className="px-4 py-3 border-b border-[var(--border)] flex items-center gap-3">
          <div className="relative flex-1">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)]"
              size={16}
            />
            <input
              type="text"
              placeholder="Search mail"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full bg-[var(--bg-element)] border border-[var(--border)] text-[var(--text-main)] pl-10 pr-4 py-2.5 rounded-lg focus:outline-none focus:border-[var(--primary)] text-sm"
            />
          </div>
          <button
            onClick={refresh}
            disabled={emailsLoading}
            className="p-2.5 hover:bg-[var(--bg-element)] rounded-lg text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw size={18} className={emailsLoading ? 'animate-spin' : ''} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {emailsLoading && emails.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[var(--primary)]" />
            </div>
          ) : filteredEmails.length === 0 ? (
            <div className="p-8 text-center text-[var(--text-muted)] text-sm">
              {searchTerm ? 'No emails match your search' : 'No emails in this folder'}
            </div>
          ) : (
            filteredEmails.map(email => (
              <div
                key={email.uid}
                onClick={() => {
                  setSelectedEmailUid(email.uid)
                  if (!email.isRead) {
                    handleMarkRead(email.uid)
                  }
                }}
                className={`px-5 py-4 border-b border-[var(--border)]/50 cursor-pointer hover:bg-[var(--bg-element)]/40 transition-colors ${
                  selectedEmailUid === email.uid
                    ? 'bg-[var(--bg-element)] border-l-2 border-l-emerald-500'
                    : 'border-l-2 border-l-transparent'
                }`}
              >
                <div className="flex justify-between items-baseline mb-1">
                  <span
                    className={`text-sm font-medium ${
                      !email.isRead ? 'text-white' : 'text-zinc-300'
                    }`}
                  >
                    {email.from.name || email.from.address}
                  </span>
                  <span className="text-[11px] text-zinc-500 whitespace-nowrap ml-2">
                    {formatDate(email.date)}
                  </span>
                </div>
                <h4
                  className={`text-sm mb-1 truncate ${
                    !email.isRead
                      ? 'text-white font-medium'
                      : 'text-zinc-400'
                  }`}
                >
                  {email.subject || '(No Subject)'}
                </h4>
                <p className="text-xs text-zinc-500 line-clamp-2">{email.snippet}</p>

                {/* Tags/Labels */}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {email.isStarred && (
                      <Star size={12} className="text-yellow-400 fill-yellow-400" />
                    )}
                    {/* Folder tag */}
                    <span className="text-[10px] px-1.5 py-0.5 bg-zinc-800 text-zinc-400 rounded">
                      {selectedFolder === 'INBOX' ? 'Inbox' : selectedFolder.split('/').pop() || 'Inbox'}
                    </span>
                  </div>
                  {/* Attachment indicator - prominent label */}
                  {email.hasAttachments && (
                    <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 bg-emerald-500/20 text-emerald-400 rounded-full border border-emerald-500/30">
                      <Paperclip size={10} />
                      Attachment
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 3. Reading Pane (Right) - Only show when email selected */}
      {selectedEmailUid && (
        <div className="flex-1 flex flex-col bg-zinc-950 relative">
          {selectedEmail ? (
            <>
              {/* Email Header */}
              <div className="h-16 flex items-center justify-between px-6 border-b border-zinc-800 bg-zinc-950/95 backdrop-blur z-10">
                <h2 className="text-lg font-semibold text-zinc-100 truncate flex-1 pr-4">
                  {selectedEmail.subject || '(No Subject)'}
                </h2>
                <div className="flex items-center gap-1 text-zinc-400">
                  <button
                    onClick={() => handleStar(selectedEmail.uid, selectedEmail.isStarred)}
                    className={`p-2 hover:bg-zinc-900 rounded transition-colors ${
                      selectedEmail.isStarred ? 'text-yellow-500' : 'hover:text-yellow-500'
                    }`}
                  >
                    <Star size={18} className={selectedEmail.isStarred ? 'fill-yellow-500' : ''} />
                  </button>
                  <button className="p-2 hover:bg-zinc-900 rounded transition-colors">
                    <Printer size={18} />
                  </button>
                  <button className="p-2 hover:bg-zinc-900 rounded transition-colors">
                    <Archive size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(selectedEmail.uid)}
                    className="p-2 hover:bg-zinc-900 rounded transition-colors hover:text-red-400"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              {/* Main Content Area (Scrollable) */}
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                <div className="p-6 pb-20">
                  {/* Sender Info */}
                  <div className="flex items-start justify-between mb-8">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-zinc-800 overflow-hidden border border-zinc-700 flex items-center justify-center text-white font-bold text-sm">
                        {(selectedEmail.from.name || selectedEmail.from.address).charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="flex items-baseline gap-2">
                          <span className="text-sm font-semibold text-zinc-100">
                            {selectedEmail.from.name || selectedEmail.from.address}
                          </span>
                          <span className="text-xs text-zinc-500">
                            &lt;{selectedEmail.from.address}&gt;
                          </span>
                        </div>
                        <div className="text-xs text-zinc-400 mt-0.5">
                          To: <span className="text-zinc-300">{selectedEmail.to.map(t => t.name || t.address).join(', ')}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-zinc-500">
                      {formatDate(selectedEmail.date, true)}
                    </div>
                  </div>

                  {/* Email Body */}
                  {emailLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                    </div>
                  ) : selectedEmail.bodyHtml ? (
                    <div className="mb-12">
                      <EmailContentFrame html={selectedEmail.bodyHtml} darkMode={true} />
                    </div>
                  ) : (
                    <div className="prose prose-invert max-w-none text-sm text-zinc-300 leading-relaxed mb-12 whitespace-pre-wrap">
                      {selectedEmail.bodyText || selectedEmail.snippet}
                    </div>
                  )}

                  {/* Attachments */}
                  {selectedEmail.hasAttachments && selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                    <div className="space-y-2 mb-8">
                      <h4 className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">
                        Attachments ({selectedEmail.attachments.length})
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedEmail.attachments.map((att, i) => (
                          <button
                            key={i}
                            onClick={() => handleDownloadAttachment(att)}
                            className="p-3 bg-zinc-900 border border-zinc-800 rounded-lg inline-flex items-center gap-3 hover:border-emerald-500/50 hover:bg-zinc-800 transition-colors cursor-pointer group"
                            title={`Download ${att.filename}`}
                          >
                            <div className="p-2 bg-zinc-800 rounded group-hover:bg-emerald-500/20 transition-colors">
                              <FileText size={16} className="text-emerald-500" />
                            </div>
                            <div className="text-left">
                              <p className="text-sm text-zinc-200 font-medium">{att.filename}</p>
                              <p className="text-xs text-zinc-500">
                                {formatFileSize(att.size)}
                              </p>
                            </div>
                            <Download size={16} className="text-zinc-500 group-hover:text-emerald-400 transition-colors ml-2" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Floating Reply Dialog */}
              <div className={`
                absolute bottom-0 left-0 right-0 z-20 transition-all duration-300 ease-in-out
                border-t border-zinc-800 bg-zinc-900/95 backdrop-blur-sm
                ${isReplyOpen ? 'shadow-2xl' : ''}
              `}>
                {/* Reply Mode Selector & Header (always visible) */}
                <div
                  className={`flex items-center justify-between px-4 py-3 ${isReplyOpen ? 'border-b border-zinc-800 bg-zinc-900' : ''}`}
                  onClick={() => !isReplyOpen && setIsReplyOpen(true)}
                >
                  <div className="flex items-center gap-2">
                    <div className="relative group">
                      <button
                        className="flex items-center text-sm font-medium text-zinc-300 hover:text-white bg-zinc-800 hover:bg-zinc-700 px-3 py-1.5 rounded-md transition-colors"
                        onClick={(e) => {
                          e.stopPropagation()
                          // Toggle through modes
                          const modes: Array<'reply' | 'replyAll' | 'forward'> = ['reply', 'replyAll', 'forward']
                          const currentIndex = modes.indexOf(replyMode)
                          setReplyMode(modes[(currentIndex + 1) % modes.length])
                        }}
                      >
                        {replyMode === 'reply' && <><Reply size={16} className="mr-2" />Reply</>}
                        {replyMode === 'replyAll' && <><ReplyAll size={16} className="mr-2" />Reply All</>}
                        {replyMode === 'forward' && <><Forward size={16} className="mr-2" />Forward</>}
                        <ChevronDown size={14} className="ml-2 opacity-50" />
                      </button>
                    </div>

                    {isReplyOpen && (
                      <>
                        <span className="text-zinc-500 text-sm">to</span>
                        <span className="bg-zinc-800/50 text-zinc-300 px-2 py-0.5 rounded text-sm border border-zinc-700/50 flex items-center gap-1">
                          {selectedEmail.from.name || selectedEmail.from.address}
                          <X size={12} className="cursor-pointer hover:text-white opacity-50 hover:opacity-100" />
                        </span>
                      </>
                    )}
                  </div>

                  {isReplyOpen && (
                    <div className="flex items-center gap-2">
                      <button className="p-1.5 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white transition-colors">
                        <Maximize2 size={16} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setIsReplyOpen(false)
                          setReplyContent('')
                        }}
                        className="p-1.5 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white transition-colors"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Expanded Reply Content */}
                {isReplyOpen && (
                  <>
                    {/* Text Area */}
                    <div className="px-4 py-3">
                      <textarea
                        ref={replyTextareaRef}
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        placeholder="Write your reply..."
                        className="w-full bg-transparent text-zinc-200 placeholder-zinc-600 resize-none focus:outline-none font-sans leading-relaxed min-h-[150px]"
                        autoFocus
                      />
                    </div>

                    {/* Toolbar */}
                    <div className="bg-zinc-900 px-4 py-3 border-t border-zinc-800 flex items-center justify-between">
                      {/* Left Tools */}
                      <div className="flex items-center gap-1">
                        <div className="flex items-center border-r border-zinc-700 pr-2 mr-2 gap-0.5">
                          <button className="p-2 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded transition-colors" title="Bold">
                            <Bold size={16} />
                          </button>
                          <button className="p-2 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded transition-colors" title="Italic">
                            <Italic size={16} />
                          </button>
                          <button className="p-2 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded transition-colors" title="Underline">
                            <Underline size={16} />
                          </button>
                        </div>

                        <button className="p-2 text-zinc-400 hover:text-emerald-400 hover:bg-zinc-800 rounded transition-colors" title="Attach">
                          <Paperclip size={18} />
                        </button>
                        <button className="p-2 text-zinc-400 hover:text-yellow-400 hover:bg-zinc-800 rounded transition-colors" title="Emoji">
                          <Smile size={18} />
                        </button>
                      </div>

                      {/* Right Actions */}
                      <div className="flex items-center gap-3">
                        <button className="text-zinc-400 hover:text-red-400 p-2 rounded-full transition-colors" title="Delete draft">
                          <Trash2 size={18} />
                        </button>

                        <div className="flex items-center">
                          <button className="bg-emerald-600 hover:bg-emerald-500 text-white font-medium px-6 py-2 rounded-l-lg transition-colors flex items-center gap-2">
                            <Send size={16} />
                            Send
                          </button>
                          <button className="bg-emerald-700 hover:bg-emerald-600 text-emerald-100 px-2 py-2 rounded-r-lg border-l border-emerald-800 transition-colors" title="Schedule">
                            <Clock size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Collapsed state hint */}
                {!isReplyOpen && (
                  <div
                    className="px-4 pb-3 cursor-text"
                    onClick={() => setIsReplyOpen(true)}
                  >
                    <div className="flex items-center text-zinc-500">
                      <Reply size={18} className="mr-3" />
                      <span>Reply to {selectedEmail.from.name || selectedEmail.from.address}...</span>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-zinc-500">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
              <p className="mt-2">Loading email...</p>
            </div>
          )}
        </div>
      )}

      {/* Compose Modal */}
      {isComposeOpen && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-2xl bg-zinc-950 border border-zinc-800 rounded-xl shadow-2xl flex flex-col overflow-hidden animate-fade-in ring-1 ring-zinc-700">
            {/* Header */}
            <div className="flex justify-between items-center px-4 py-3 border-b border-zinc-800 bg-zinc-900">
              <h3 className="font-semibold text-zinc-100">New Message</h3>
              <div className="flex items-center gap-2">
                <button className="p-1.5 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white transition-colors">
                  <Maximize2 size={16} />
                </button>
                <button
                  onClick={() => setIsComposeOpen(false)}
                  className="p-1.5 hover:bg-zinc-800 rounded text-zinc-400 hover:text-white transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Recipients */}
            <div className="border-b border-zinc-800">
              <div className="flex items-center px-4 py-2 border-b border-zinc-800/50">
                <span className="text-sm text-zinc-500 w-16">From:</span>
                <span className="text-sm text-zinc-300">
                  {selectedAccount?.emailAddress || 'Select an account'}
                </span>
              </div>
              <div className="flex items-center px-4 py-2 border-b border-zinc-800/50">
                <span className="text-sm text-zinc-500 w-16">To:</span>
                <input
                  type="text"
                  placeholder="Recipients"
                  value={composeData.to}
                  onChange={e => setComposeData(prev => ({ ...prev, to: e.target.value }))}
                  className="flex-1 bg-transparent text-sm text-zinc-200 focus:outline-none placeholder-zinc-600"
                />
              </div>
              <div className="flex items-center px-4 py-2 border-b border-zinc-800/50">
                <span className="text-sm text-zinc-500 w-16">Cc:</span>
                <input
                  type="text"
                  placeholder="Add Cc recipients"
                  value={composeData.cc}
                  onChange={e => setComposeData(prev => ({ ...prev, cc: e.target.value }))}
                  className="flex-1 bg-transparent text-sm text-zinc-200 focus:outline-none placeholder-zinc-600"
                />
              </div>
              <div className="flex items-center px-4 py-2">
                <span className="text-sm text-zinc-500 w-16">Subject:</span>
                <input
                  type="text"
                  placeholder="Subject"
                  value={composeData.subject}
                  onChange={e => setComposeData(prev => ({ ...prev, subject: e.target.value }))}
                  className="flex-1 bg-transparent text-sm text-zinc-200 focus:outline-none placeholder-zinc-600"
                />
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 p-4">
              <textarea
                className="w-full h-64 bg-transparent text-sm text-zinc-200 placeholder-zinc-600 resize-none focus:outline-none leading-relaxed"
                placeholder="Write your message..."
                value={composeData.body}
                onChange={e => setComposeData(prev => ({ ...prev, body: e.target.value }))}
              />
            </div>

            {/* Toolbar */}
            <div className="px-4 py-3 border-t border-zinc-800 flex justify-between items-center bg-zinc-900">
              <div className="flex items-center gap-1">
                <div className="flex items-center border-r border-zinc-700 pr-2 mr-2 gap-0.5">
                  <button className="p-2 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded transition-colors" title="Bold">
                    <Bold size={16} />
                  </button>
                  <button className="p-2 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded transition-colors" title="Italic">
                    <Italic size={16} />
                  </button>
                  <button className="p-2 text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded transition-colors" title="Underline">
                    <Underline size={16} />
                  </button>
                </div>
                <button className="p-2 text-zinc-400 hover:text-emerald-400 hover:bg-zinc-800 rounded transition-colors" title="Attach">
                  <Paperclip size={18} />
                </button>
                <button className="p-2 text-zinc-400 hover:text-yellow-400 hover:bg-zinc-800 rounded transition-colors" title="Emoji">
                  <Smile size={18} />
                </button>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsComposeOpen(false)}
                  className="text-zinc-400 hover:text-red-400 p-2 rounded-full transition-colors"
                  title="Discard"
                >
                  <Trash2 size={18} />
                </button>

                <div className="flex items-center">
                  <button
                    onClick={handleSendEmail}
                    disabled={sendingEmail || !composeData.to || !composeData.subject}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white font-medium px-6 py-2 rounded-l-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingEmail ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                    Send
                  </button>
                  <button className="bg-emerald-700 hover:bg-emerald-600 text-emerald-100 px-2 py-2 rounded-r-lg border-l border-emerald-800 transition-colors" title="Schedule">
                    <Clock size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Helper functions
function formatDate(dateStr: string, full = false): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

  if (full) {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  if (diffDays === 0) {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  } else if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'short' })
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
